(() => {
  'use strict';

  const SOURCE = 'misk-gift-offer';
  const ROOT = window.Shopify?.routes?.root || '/';
  const GIFT_VARIANTS = {
    ajwad: 48826963853544,
    outdoor: 48826964017384,
  };
  const GIFT_HANDLES = new Set([
    'ajwad-bakhoor-free-gift',
    'outdoor-signature-box-free-gift',
  ]);
  const GIFT_VARIANT_IDS = new Set(Object.values(GIFT_VARIANTS));
  const GIFT_VALUES = {
    ajwad: '₹300',
    outdoor: '₹750',
  };
  const nativeFetch = window.fetch.bind(window);
  const NativeXMLHttpRequest = window.XMLHttpRequest;

  let syncing = false;
  let queued = false;
  let timer = 0;
  let observer;

  function isCartMutationUrl(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/i.test(String(url || ''));
  }

  function giftDetailsFromLink(link) {
    const href = String(link?.getAttribute('href') || '');
    if (href.includes('ajwad-bakhoor-free-gift')) {
      return { variantId: GIFT_VARIANTS.ajwad, value: GIFT_VALUES.ajwad };
    }
    if (href.includes('outdoor-signature-box-free-gift')) {
      return { variantId: GIFT_VARIANTS.outdoor, value: GIFT_VALUES.outdoor };
    }
    return null;
  }

  const cartEndpoint = (path) => `${ROOT}${path}`.replace(/\/+/g, '/');

  async function requestCart(path, body) {
    const response = await nativeFetch(cartEndpoint(path), {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: body
        ? {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          }
        : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      throw new Error(`Misk gift cart request failed: ${response.status}`);
    }

    return response.json();
  }

  function lineVolume(item) {
    if (GIFT_VARIANT_IDS.has(Number(item.variant_id))) return 0;
    if (String(item.product_type || '').toLowerCase() === 'promotional gift') return 0;

    const variantTitle = String(item.variant_title || '').trim();
    const sizeMatch = variantTitle.match(/^(50|100)\s*ml$/i);
    if (!sizeMatch) return 0;

    return Number(sizeMatch[1]) * Number(item.quantity || 0);
  }

  function desiredGift(cart) {
    const totalMl = (cart.items || []).reduce((sum, item) => sum + lineVolume(item), 0);

    if (totalMl >= 200) return GIFT_VARIANTS.outdoor;
    if (totalMl >= 100) return GIFT_VARIANTS.ajwad;
    return null;
  }

  function isGift(item) {
    return GIFT_VARIANT_IDS.has(Number(item.variant_id)) || GIFT_HANDLES.has(String(item.handle || ''));
  }

  async function removeLine(item) {
    await requestCart('cart/change.js', { id: item.key, quantity: 0 });
  }

  async function setLineToOne(item) {
    await requestCart('cart/change.js', { id: item.key, quantity: 1 });
  }

  async function addGift(variantId) {
    await requestCart('cart/add.js', {
      items: [
        {
          id: variantId,
          quantity: 1,
          properties: {
            _misk_free_gift: 'true',
          },
        },
      ],
    });
  }

  function hideProductBuyNowButtons() {
    const productForm = document.querySelector('main form[action*="/cart/add"]');
    if (!productForm) return;

    const productScope =
      productForm.closest('product-info, [id*="ProductInfo"], [data-product-id], .product, section') ||
      productForm.parentElement;
    if (!productScope) return;

    productScope
      .querySelectorAll(
        '.shopify-payment-button, shopify-accelerated-checkout, [data-shopify="payment-button"], [data-testid="Checkout-button"], [data-buy-now-button], [class*="magic-checkout" i], [id*="magic-checkout" i], [class*="razorpay" i]'
      )
      .forEach((element) => {
        element.classList.add('misk-hidden-buy-now');
        element.setAttribute('aria-hidden', 'true');
      });

    productScope.querySelectorAll('button, a').forEach((element) => {
      const label = `${element.textContent || ''} ${element.getAttribute('aria-label') || ''}`
        .replace(/\s+/g, ' ')
        .trim();
      if (/\b(?:buy\s+(?:it\s+)?now|magic\s+checkout|buy\s+with)\b/i.test(label)) {
        element.classList.add('misk-hidden-buy-now');
        element.setAttribute('aria-hidden', 'true');
      }
    });
  }

  function dispatchCartUpdate(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: SOURCE,
          data: {
            itemCount: Number(cart.item_count || 0),
            source: SOURCE,
          },
        },
      })
    );
  }

  function decorateGiftRows() {
    const giftLinks = document.querySelectorAll(
      'a[href*="/products/ajwad-bakhoor-free-gift"], a[href*="/products/outdoor-signature-box-free-gift"]'
    );

    giftLinks.forEach((link) => {
      const row = link.closest('tr, [data-key], .cart-item');
      if (!row) return;
      const gift = giftDetailsFromLink(link);
      if (!gift) return;

      row.classList.add('misk-free-gift-line');

      row.querySelectorAll('input[type="number"], input[name="updates[]"], input[data-cart-quantity]').forEach((input) => {
        input.value = '1';
        input.readOnly = true;
        input.setAttribute('aria-readonly', 'true');
        input.tabIndex = -1;
      });

      row.querySelectorAll('button').forEach((button) => {
        const label = `${button.getAttribute('aria-label') || ''} ${button.textContent || ''}`;
        if (/increase|decrease|remove/i.test(label)) {
          button.disabled = true;
          button.hidden = true;
          button.setAttribute('aria-hidden', 'true');
          button.tabIndex = -1;
        }
      });

      const infoCell = link.closest('td, [class*="cart-item__details"], [class*="product-information"]') || row;
      if (!infoCell.querySelector('.misk-free-gift-badge')) {
        const badge = document.createElement('span');
        badge.className = 'misk-free-gift-badge';
        badge.textContent = 'FREE GIFT · QTY 1';
        infoCell.appendChild(badge);
      }

      let worth = infoCell.querySelector('.misk-free-gift-worth');
      if (!worth) {
        worth = document.createElement('span');
        worth.className = 'misk-free-gift-worth';
        infoCell.appendChild(worth);
      }
      const worthText = `Gift worth ${gift.value}`;
      if (worth.textContent !== worthText) worth.textContent = worthText;

      row.querySelectorAll(
        '.price, .cart-item__price, .cart-item__totals, [class*="price"], [data-cart-item-price]'
      ).forEach((priceNode) => {
        if (priceNode.closest('.misk-free-gift-worth')) return;
        const priceText = String(priceNode.textContent || '').replace(/\s+/g, ' ').trim();
        if (/(?:₹|rs\.?|inr)\s*0(?:[.,]00)?\b|\b0(?:[.,]00)?\s*(?:inr)\b/i.test(priceText)) {
          priceNode.classList.add('misk-free-gift-zero-price');
          priceNode.setAttribute('aria-hidden', 'true');
        }
      });
    });
  }

  function hideGiftRowsExcept(wantedVariant) {
    const giftLinks = document.querySelectorAll(
      'a[href*="/products/ajwad-bakhoor-free-gift"], a[href*="/products/outdoor-signature-box-free-gift"]'
    );

    giftLinks.forEach((link) => {
      const row = link.closest('tr, [data-key], .cart-item');
      if (!row) return;

      const href = link.getAttribute('href') || '';
      const isWanted =
        (wantedVariant === GIFT_VARIANTS.ajwad && href.includes('ajwad-bakhoor-free-gift')) ||
        (wantedVariant === GIFT_VARIANTS.outdoor && href.includes('outdoor-signature-box-free-gift'));

      if (!isWanted) row.style.display = 'none';
    });
  }

  async function syncGift() {
    if (syncing) {
      queued = true;
      return;
    }

    syncing = true;
    queued = false;

    try {
      let cart = await requestCart('cart.js');
      const wantedVariant = desiredGift(cart);
      const giftLines = (cart.items || []).filter(isGift);
      let keptWantedLine = null;
      let changed = false;

      for (const item of giftLines) {
        if (wantedVariant && Number(item.variant_id) === wantedVariant && !keptWantedLine) {
          keptWantedLine = item;
          if (Number(item.quantity) !== 1) {
            await setLineToOne(item);
            changed = true;
          }
        } else {
          await removeLine(item);
          changed = true;
        }
      }

      if (wantedVariant && !keptWantedLine) {
        await addGift(wantedVariant);
        changed = true;
      }

      if (changed) {
        cart = await requestCart('cart.js');
        dispatchCartUpdate(cart);
      }

      decorateGiftRows();
    } catch (error) {
      console.error('[Misk offer]', error);
    } finally {
      syncing = false;
      if (queued) scheduleSync(0);
    }
  }

  function scheduleSync(delay = 60) {
    window.clearTimeout(timer);
    timer = window.setTimeout(syncGift, delay);
  }

  function beforeExternalCartMutation() {
    // Safari can delay the theme's cart event. Remove a stale gift from view
    // immediately, then let syncGift reconcile the server cart after the request.
    hideGiftRowsExcept(null);
  }

  window.fetch = function miskGiftAwareFetch(input, init) {
    const url = typeof input === 'string' ? input : input?.url;
    const isExternalCartMutation = !syncing && isCartMutationUrl(url);

    if (isExternalCartMutation) beforeExternalCartMutation();

    const promise = nativeFetch(input, init);
    if (isExternalCartMutation) {
      promise.then(() => scheduleSync(0), () => scheduleSync(0));
    }
    return promise;
  };

  if (NativeXMLHttpRequest) {
    const nativeXHROpen = NativeXMLHttpRequest.prototype.open;
    const nativeXHRSend = NativeXMLHttpRequest.prototype.send;

    NativeXMLHttpRequest.prototype.open = function (method, url) {
      this._miskCartMutation = !syncing && isCartMutationUrl(url);
      return nativeXHROpen.apply(this, arguments);
    };

    NativeXMLHttpRequest.prototype.send = function () {
      if (this._miskCartMutation) {
        beforeExternalCartMutation();
        this.addEventListener('loadend', () => scheduleSync(0), { once: true });
      }
      return nativeXHRSend.apply(this, arguments);
    };
  }

  document.addEventListener('cart:update', (event) => {
    if (event.detail?.data?.source === SOURCE || event.detail?.sourceId === SOURCE) return;

    const updatedCart = event.detail?.resource;
    if (Array.isArray(updatedCart?.items)) {
      hideGiftRowsExcept(desiredGift(updatedCart));
    }

    scheduleSync(40);
  });

  document.addEventListener('DOMContentLoaded', () => {
    scheduleSync(0);
    hideProductBuyNowButtons();
    observer = new MutationObserver(() => {
      decorateGiftRows();
      hideProductBuyNowButtons();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });

  window.addEventListener('pageshow', () => scheduleSync(0));
})();
